import { API_ROUTES } from '@/api/apiRoutes';
import { GENERATOR_MODEL_SLUGS, type GeneratorModelSlug } from '@/consts/generatorModelSlugs';
import { safeAxios } from '@/services/safeAxios';
import { validateParameters, validateParameterValue } from '@/utils/nodeInputValidation';
import { isFreePlanBlockedModelNode } from '@/utils/free-plan-blocked-models.utils';
import { optionsForVariant, settingsHiddenForVariant } from '@/utils/modelSettingRules';
import { ModelType } from '@/enums/model-type.enum';
import {
  GPT_IMAGE_25_FLARE_VARIANT,
  GPT_IMAGE_25_SUNBURST_VARIANT,
} from '@/components/Nodes/ModelComponents/consts/gpt-image-25.const';
import { KLING_3_PRO_VARIANT, KLING_3_STANDARD_VARIANT } from '@/components/Nodes/ModelComponents/consts/kling-3.const';
import {
  NANO_BANANA_2_MODEL,
  NANO_BANANA_2_VARIANT,
  NANO_BANANA_21_VARIANT,
} from '@/components/Nodes/ModelComponents/consts/nano-banana-2.const';
import {
  SEEDANCE_2_FAST_VARIANT,
  SEEDANCE_2_MODEL,
  SEEDANCE_2_REFERENCE_MODEL,
  SEEDANCE_2_STANDARD_VARIANT,
} from '@/components/Nodes/ModelComponents/consts/seedance-2.const';
import type { ModelRunCredits } from '@/api/modelPrediction';
import type { MediaAsset, ResolvedAssets } from '@/types/api/assets';
import type { NodeDefinition } from '@/types/api/nodeDefinition';
import type { Schema } from '@/types/node';
import type { ModelBaseNodeData } from '@/types/nodes/model';
import type { ParameterConstraint, ParameterSchema, ParameterValue } from 'web';

export type GenerateMode = 'image' | 'video';

/** A seed as the run input takes it: `isRandom` asks the server for a fresh seed on every run. */
export type GenerateSeed = { seed: number; isRandom: boolean };

export type GenerateParamValue = string | boolean | number | GenerateSeed;

/** A settings menu in the composer footer, in footer order. */
export type GeneratePill = 'resolution' | 'quality' | 'aspectRatio' | 'duration' | 'audio';

export const isGenerateSeed = (value: unknown): value is GenerateSeed =>
  typeof value === 'object' &&
  value !== null &&
  'seed' in value &&
  typeof value.seed === 'number' &&
  'isRandom' in value &&
  typeof value.isRandom === 'boolean';

/**
 * A setting of the model's schema. A primary one is an `enum` offered as a dropdown, a `boolean` offered as
 * On / Off, or a bounded `integer` offered as a slider; `duration` is always a slider, also when its schema is
 * an `enum`. The More Settings panel offers the other types as well.
 */
export type GenerateParam = {
  /** The schema key, which is also the key the run input carries. */
  key: string;
  title: string;
  /** Set on a primary param: the footer menu it fills. */
  pill?: GeneratePill;
} & (
  | { type: 'enum'; options: readonly string[]; defaultValue: string }
  | { type: 'boolean'; defaultValue: boolean }
  | {
      type: 'integer';
      min: number;
      max: number;
      defaultValue: number;
      /**
       * Set when the schema is an `enum` of whole numbers: the only values the model accepts, ascending.
       * The run sends the chosen one as a string, which is what the `enum` holds.
       */
      options?: readonly number[];
      /** The `enum` option that lets the model choose, such as Seedance's `auto` duration. */
      auto?: string;
      /** The definition's saved value is {@link auto}, so a user whose plan allows it starts there. */
      startsAuto?: boolean;
    }
  | { type: 'float'; min: number; max: number; defaultValue: number }
  /** A number field with no bounds, so no slider. */
  | { type: 'number'; decimals: 0 | 2; defaultValue: number }
  | { type: 'seed'; defaultValue: GenerateSeed }
  | { type: 'string'; defaultValue: string }
);

/** The kind of media a reference or frame can be. */
export type GenerateMediaType = 'image' | 'video' | 'audio';

/**
 * The media a model takes besides the prompt. None of it is needed to run, except that a last frame needs a first
 * frame.
 */
export type GenerateInputs = {
  /** One image in each slot. Each value is the key the run sends the image under. */
  frames?: { first: string; last?: string };
  references?: {
    /**
     * The run sends the references as `<keyPrefix>_1`, `<keyPrefix>_2` and so on, in the order they were added.
     * The server reads these keys whether or not the definition lists that many handles.
     */
    keyPrefix: string;
    types: readonly GenerateMediaType[];
    /** For all references together. A first frame takes one of these places too. */
    max: number;
    /** A smaller limit for one type, inside {@link max}. */
    maxByType?: Readonly<Partial<Record<GenerateMediaType, number>>>;
  };
};

/** A model the generate page can run. */
export type GenerateModel = {
  /** The model's `node_definitions.id`, which a model run accepts. Empty until node definitions load. */
  id: string;
  /** Environment-independent name the page's `?model=` param carries. */
  slug: string;
  /** Provider model name, such as `fal-ai/nano-banana-2/edit`. */
  modelName: string;
  /** The version this menu row runs, for a definition that serves several. */
  variant?: string;
  name: string;
  /** Node definition `icon` key (e.g. `openai`, `gemini`), resolved the same way as the nodes menu. */
  icon: string;
  /** What the model makes. The Image / Video toggle lists the models of one mode. */
  mode: GenerateMode;
  /** Primary params, in {@link PILLS} order. */
  params: readonly GenerateParam[];
  /** The other settings, in schema order, offered in the More Settings panel. */
  moreParams: readonly GenerateParam[];
  /**
   * What a run starts from: the definition's saved values with the version on top. The user's settings and the
   * prompt go on top of these, as in a canvas run, so a setting the user doesn't change runs with the value the
   * panel shows, and a setting hidden from the panel still runs with its saved value.
   */
  baseInput: Readonly<Record<string, unknown>>;
  /** Blocked on the free plan whatever its output, such as GPT Image 2.5. */
  freePlanBlocked: boolean;
  maxCount: number;
  inputs: GenerateInputs;
};

/**
 * What the definition can't tell the generate page about one menu row. Model rules that hold on the canvas too,
 * such as which versions a flag allows, live in `modelSettingRules`, not here.
 */
type GenerateModelEntry = {
  /**
   * Menu name and icon, signed in or out. Signed-out visitors do not load node definitions, and a definition's
   * own label can be off: an imported one, such as Flux 2 Pro in dev, has only a generic icon and no maker logo.
   */
  name: string;
  icon: string;
  mode: GenerateMode;
  /** `model.name` of the built-in definition, which is the same in every database, unlike its id. */
  modelName: string;
  /** The `model` setting this row always sends and never shows, for a definition that serves several versions. */
  variant?: string;
  /** Settings the generate page doesn't offer. They still run with the definition's saved value. */
  hidden?: readonly string[];
  /** Known without the definition, so signed-out visitors see the same slots. */
  inputs: GenerateInputs;
};

/** The page never takes more references than this, even when the model takes more. */
const MAX_REFERENCES = 10;

/** The image models read `image_N` keys, which the server joins into one list. */
const IMAGE_REFERENCES: GenerateInputs = {
  references: { keyPrefix: 'image', types: ['image'], max: MAX_REFERENCES },
};

const SEEDANCE_2_FRAMES: GenerateInputs = { frames: { first: 'image_url', last: 'end_image_url' } };

const SEEDANCE_REFERENCES: GenerateInputs = {
  references: { keyPrefix: 'reference', types: ['image', 'video', 'audio'], max: MAX_REFERENCES },
};

/** Core moves `image` to the Kling 3.0 endpoint's start image. */
const KLING_3_FRAMES: GenerateInputs = { frames: { first: 'image', last: 'end_image_url' } };

/**
 * The models the menu offers, keyed by the slug the page's `?model=` param carries. The menu sorts them by name,
 * so the order here doesn't matter.
 */
const MODELS_BY_SLUG: Readonly<Partial<Record<GeneratorModelSlug, GenerateModelEntry>>> = {
  // Image models
  [GENERATOR_MODEL_SLUGS.FLUX_3_IMAGE]: {
    name: 'Flux 3',
    icon: 'black_forest',
    mode: 'image',
    modelName: 'blackforestlabs/flux-3/text-to-image',
    inputs: IMAGE_REFERENCES,
  },
  [GENERATOR_MODEL_SLUGS.GPT_IMAGE_2_5_FLARE]: {
    name: 'GPT Image 2.5 - Flare',
    icon: 'openai',
    mode: 'image',
    modelName: ModelType.GptImage25,
    variant: GPT_IMAGE_25_FLARE_VARIANT,
    inputs: {},
  },
  [GENERATOR_MODEL_SLUGS.GPT_IMAGE_2_5_SUNBURST]: {
    name: 'GPT Image 2.5 - Sunburst',
    icon: 'openai',
    mode: 'image',
    modelName: ModelType.GptImage25,
    variant: GPT_IMAGE_25_SUNBURST_VARIANT,
    inputs: {},
  },
  [GENERATOR_MODEL_SLUGS.IDEOGRAM_V4_5]: {
    name: 'Ideogram V4.5',
    icon: 'ideogram',
    mode: 'image',
    modelName: 'ideogram/v4.5',
    inputs: {},
  },
  [GENERATOR_MODEL_SLUGS.NANO_BANANA_PRO]: {
    name: 'Nano Banana Pro',
    icon: 'gemini',
    mode: 'image',
    modelName: 'fal-ai/nano-banana-pro/edit',
    inputs: IMAGE_REFERENCES,
  },
  [GENERATOR_MODEL_SLUGS.NANO_BANANA_2]: {
    name: 'Nano Banana 2',
    icon: 'gemini',
    mode: 'image',
    modelName: NANO_BANANA_2_MODEL,
    variant: NANO_BANANA_2_VARIANT,
    inputs: {},
  },
  [GENERATOR_MODEL_SLUGS.NANO_BANANA_2_1]: {
    name: 'Nano Banana 2.1',
    icon: 'gemini',
    mode: 'image',
    modelName: NANO_BANANA_2_MODEL,
    variant: NANO_BANANA_21_VARIANT,
    inputs: IMAGE_REFERENCES,
  },

  // Video models
  [GENERATOR_MODEL_SLUGS.SEEDANCE_2_5]: {
    name: 'Seedance 2.5',
    icon: 'bytedance',
    mode: 'video',
    modelName: 'bytedance/seedance-2.5/image-to-video',
    // No Generate high res here, so a draft would stay at 480p.
    hidden: ['draft'],
    inputs: { frames: { first: 'image_url', last: 'end_image_url' } },
  },
  [GENERATOR_MODEL_SLUGS.SEEDANCE_2_5_REFERENCE]: {
    name: 'Seedance 2.5 Reference',
    icon: 'bytedance',
    mode: 'video',
    modelName: 'bytedance/seedance-2.5/reference-to-video',
    hidden: ['draft'],
    inputs: SEEDANCE_REFERENCES,
  },
  // The canvas hides the seed on every Seedance 2.0 node.
  [GENERATOR_MODEL_SLUGS.SEEDANCE_2_0]: {
    name: 'Seedance 2.0',
    icon: 'bytedance',
    mode: 'video',
    modelName: SEEDANCE_2_MODEL,
    variant: SEEDANCE_2_STANDARD_VARIANT,
    hidden: ['seed'],
    inputs: SEEDANCE_2_FRAMES,
  },
  [GENERATOR_MODEL_SLUGS.SEEDANCE_2_0_FAST]: {
    name: 'Seedance 2.0 Fast',
    icon: 'bytedance',
    mode: 'video',
    modelName: SEEDANCE_2_MODEL,
    variant: SEEDANCE_2_FAST_VARIANT,
    // The server rejects US servers only on Fast, which has no US endpoint.
    hidden: ['seed', 'us_hosted'],
    inputs: SEEDANCE_2_FRAMES,
  },
  [GENERATOR_MODEL_SLUGS.SEEDANCE_2_0_REFERENCE]: {
    name: 'Seedance 2.0 Reference',
    icon: 'bytedance',
    mode: 'video',
    modelName: SEEDANCE_2_REFERENCE_MODEL,
    variant: SEEDANCE_2_STANDARD_VARIANT,
    hidden: ['seed'],
    inputs: SEEDANCE_REFERENCES,
  },
  [GENERATOR_MODEL_SLUGS.SEEDANCE_2_0_REFERENCE_FAST]: {
    name: 'Seedance 2.0 Reference Fast',
    icon: 'bytedance',
    mode: 'video',
    modelName: SEEDANCE_2_REFERENCE_MODEL,
    variant: SEEDANCE_2_FAST_VARIANT,
    // The server rejects US servers only on Fast, which has no US endpoint.
    hidden: ['seed', 'us_hosted'],
    inputs: SEEDANCE_REFERENCES,
  },
  [GENERATOR_MODEL_SLUGS.KLING_4_0]: {
    name: 'Kling 4.0',
    icon: 'kling',
    mode: 'video',
    modelName: 'kling/v4/text-to-video',
    inputs: { frames: { first: 'start_image_url', last: 'end_image_url' } },
  },
  [GENERATOR_MODEL_SLUGS.KLING_4_0_REFERENCE]: {
    name: 'Kling 4.0 Reference',
    icon: 'kling',
    mode: 'video',
    modelName: 'kling/v4/reference-to-video',
    // The server refuses audio, and more than 5 videos or 10 images counting the start image.
    inputs: {
      frames: { first: 'start_image_url' },
      references: { keyPrefix: 'reference', types: ['image', 'video'], max: MAX_REFERENCES, maxByType: { video: 5 } },
    },
  },
  // Kling 3.0's API takes no aspect ratio, so the canvas drops it from the node.
  [GENERATOR_MODEL_SLUGS.KLING_3_0_STANDARD]: {
    name: 'Kling 3.0 Standard',
    icon: 'kling',
    mode: 'video',
    modelName: ModelType.Kling,
    variant: KLING_3_STANDARD_VARIANT,
    hidden: ['aspect_ratio', 'shot_type'],
    inputs: KLING_3_FRAMES,
  },
  [GENERATOR_MODEL_SLUGS.KLING_3_0_PRO]: {
    name: 'Kling 3.0 Pro',
    icon: 'kling',
    mode: 'video',
    modelName: ModelType.Kling,
    variant: KLING_3_PRO_VARIANT,
    hidden: ['aspect_ratio', 'shot_type'],
    inputs: KLING_3_FRAMES,
  },
};

/** A to Z by the menu name, with numbers in order, so Kling 3.0 comes before Kling 4.0. */
const MODEL_ENTRIES = Object.entries(MODELS_BY_SLUG).sort(([, a], [, b]) =>
  a.name.localeCompare(b.name, 'en', { numeric: true }),
);

/** The setting that picks a version when one definition serves several, by the model guide's convention. */
const VARIANT_KEY = 'model';

/** The same for every model. */
const MAX_COUNT = 10;

/** The parts of a public node definition's `data` the generate page reads. */
type GenerateNodeDefinitionData = NodeDefinition['data'] & {
  menu?: { displayName?: string; icon?: string };
  model?: { name?: string };
  params?: ModelBaseNodeData['params'];
  schema?: ModelBaseNodeData['schema'];
};

/** The schema field each pill reads. */
const PILL_FIELDS: Readonly<Record<GeneratePill, string>> = {
  resolution: 'resolution',
  quality: 'quality',
  aspectRatio: 'aspect_ratio',
  duration: 'duration',
  audio: 'generate_audio',
};
/**
 * The constraint a pill reads instead when the model has no field of its own: a Fal model, such as GPT Image
 * 2.5, keeps its aspect ratio in an image size preset.
 */
const PILL_FALLBACK_CONSTRAINTS: Readonly<Partial<Record<GeneratePill, ParameterConstraint['type']>>> = {
  aspectRatio: 'image_size',
};
const PILLS: readonly GeneratePill[] = ['resolution', 'quality', 'aspectRatio', 'duration', 'audio'];

/** The canvas falls back to a fixed seed; the generate page leaves an unset seed to the server. */
const RANDOM_SEED: GenerateSeed = { seed: 1, isRandom: true };

const WHOLE_NUMBER = /^\d+$/;

/** The `enum` option that leaves the value to the model, such as `auto` duration. */
const AUTO_OPTION = 'auto';

/** Fal image size options that switch the canvas to a free width and height, which the composer has no field for. */
const isCustomImageSize = (option: string) => option === 'Default' || option.toLowerCase() === 'custom';

const getPublicNodeDefinitions = () => safeAxios.get<NodeDefinition[]>(API_ROUTES.nodeDefinitions.public());

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const numberOf = (value: ParameterValue | undefined) =>
  value?.type === 'integer' || value?.type === 'float' ? value.value : 0;

/** An `enum` value: a string option, or an integer one when the options are numbers. */
const optionOf = (value: ParameterValue | undefined) =>
  value?.type === 'string' || value?.type === 'integer' ? String(value.value) : undefined;

/**
 * A slider over the whole-number options of an `enum`, such as Seedance's `duration`. Options that are not
 * whole numbers, such as `auto`, have no place on a slider and are left out. It starts at the first of
 * `candidates` that is one of them. Null when no option is a number.
 */
function toEnumSliderParam(
  key: string,
  title: string,
  options: readonly string[],
  candidates: readonly unknown[],
): GenerateParam | null {
  const numbers = [...new Set(options.filter((option) => WHOLE_NUMBER.test(option)).map(Number))].sort((a, b) => a - b);
  if (numbers.length === 0) return null;
  const preferred = candidates.map(Number).find((candidate) => numbers.includes(candidate));
  return {
    type: 'integer',
    key,
    title,
    min: numbers[0],
    max: numbers[numbers.length - 1],
    defaultValue: preferred ?? numbers[0],
    options: numbers,
    ...(options.includes(AUTO_OPTION) ? { auto: AUTO_OPTION, startsAuto: candidates[0] === AUTO_OPTION } : {}),
  };
}

/**
 * A primary setting, or null for a type the composer footer has no menu for: a dropdown for an `enum` or a Fal
 * image size's presets, On / Off for a `boolean`, and a slider for a bounded `integer` or a duration `enum`. It
 * starts where a new canvas node does: the definition's param value, then the schema default.
 */
function toPrimaryParam(
  pill: GeneratePill,
  { id: key, constraint }: ParameterSchema,
  field: Schema,
  raw: unknown,
): GenerateParam | null {
  const title = field.title || key;
  const value = validateParameterValue(raw, field.default, field);
  if (constraint.type === 'boolean') {
    return { type: 'boolean', key, title, defaultValue: value?.type === 'boolean' && value.value };
  }
  if (constraint.type === 'integer_with_limits') {
    const { min, max } = constraint;
    if (min > max) return null;
    const preferred = value?.type === 'integer' ? value.value : min;
    return { type: 'integer', key, title, min, max, defaultValue: clamp(preferred, min, max) };
  }
  if (constraint.type === 'image_size') {
    const options = constraint.options.filter((option) => !isCustomImageSize(option));
    if (options.length === 0) return null;
    const preset = value?.type === 'image_size' && value.value.type === 'built_in' ? value.value.value : undefined;
    return {
      type: 'enum',
      key,
      // Some rows, such as Ideogram V4.5's, title it "Image Size", but here it fills the aspect ratio pill.
      title: 'Aspect Ratio',
      options,
      defaultValue: options.find((option) => option === preset) ?? options[0],
    };
  }
  if (field.type !== 'enum' || (constraint.type !== 'enum' && constraint.type !== 'integer_selector')) return null;
  const options = constraint.options.map(String);
  if (options.length === 0) return null;
  const slider = pill === 'duration' && toEnumSliderParam(key, title, options, [value?.value, field.default]);
  if (slider) return slider;
  const defaultValue = options.find((option) => option === optionOf(value)) ?? options[0];
  return { type: 'enum', key, title, options, defaultValue };
}

/**
 * A setting for the More Settings panel, or null for a type the panel has no control for (arrays, image
 * sizes, files and vectors). Like a primary setting, it starts where a new canvas node does: the definition's
 * param value, then the schema default, then the canvas fallback for the type.
 */
function toMoreParam({ id: key, constraint }: ParameterSchema, field: Schema, raw: unknown): GenerateParam | null {
  const title = field.title || key;
  if (constraint.type === 'seed') {
    return { type: 'seed', key, title, defaultValue: [raw, field.default].find(isGenerateSeed) ?? RANDOM_SEED };
  }
  const value = validateParameterValue(raw, field.default, field);
  switch (constraint.type) {
    case 'boolean':
      return { type: 'boolean', key, title, defaultValue: value?.type === 'boolean' && value.value };
    case 'string':
      return { type: 'string', key, title, defaultValue: value?.type === 'string' ? value.value : '' };
    case 'enum':
    case 'integer_selector': {
      if (field.type !== 'enum') return { type: 'number', key, title, decimals: 0, defaultValue: numberOf(value) };
      const options = constraint.options.map(String);
      if (options.length === 0) return null;
      const defaultValue = options.find((option) => option === optionOf(value)) ?? options[0];
      return { type: 'enum', key, title, options, defaultValue };
    }
    case 'integer':
    case 'float': {
      const decimals = constraint.type === 'integer' ? 0 : 2;
      return { type: 'number', key, title, decimals, defaultValue: numberOf(value) };
    }
    case 'integer_with_limits':
    case 'float_with_limits': {
      const { min, max } = constraint;
      const integer = constraint.type === 'integer_with_limits';
      if (!isNumber(min) || !isNumber(max) || min >= max) {
        return { type: 'number', key, title, decimals: integer ? 0 : 2, defaultValue: numberOf(value) };
      }
      const defaultValue = clamp(numberOf(value), min, max);
      return integer
        ? { type: 'integer', key, title, min, max, defaultValue }
        : { type: 'float', key, title, min, max, defaultValue };
    }
    default:
      return null;
  }
}

/**
 * The composer's settings, from the parameters a new canvas node gets: every schema field that is not an
 * input handle, unless it is exposed as one. The primary ones come in {@link PILLS} order, and the others, for
 * the More Settings panel, in schema `order`. Leaves out what the entry hides, the version it fixes, and the
 * settings only another version takes.
 */
function toGenerateParams(
  entry: GenerateModelEntry,
  data: GenerateNodeDefinitionData,
): Pick<GenerateModel, 'params' | 'moreParams'> {
  const schema = data.schema ?? {};
  const hidden = new Set([
    ...(entry.hidden ?? []),
    ...(entry.variant ? [VARIANT_KEY] : []),
    ...settingsHiddenForVariant(entry.modelName, entry.variant),
  ]);
  const parameters = validateParameters(data.handles?.input, data.params, schema).filter(
    (parameter) => !hidden.has(parameter.id),
  );
  const byKey = new Map(parameters.map((parameter) => [parameter.id, parameter]));
  const forVariant = (param: GenerateParam): GenerateParam => {
    if (param.type !== 'enum') {
      return param;
    }
    const options = optionsForVariant(entry.modelName, entry.variant, param.key, param.options);
    return { ...param, options, defaultValue: options.includes(param.defaultValue) ? param.defaultValue : options[0] };
  };
  const findPillParameter = (pill: GeneratePill) => {
    const fallbackConstraint = PILL_FALLBACK_CONSTRAINTS[pill];
    return (
      byKey.get(PILL_FIELDS[pill]) ??
      (fallbackConstraint && parameters.find(({ constraint }) => constraint.type === fallbackConstraint))
    );
  };
  const params = PILLS.flatMap((pill) => {
    const parameter = findPillParameter(pill);
    const param = parameter && toPrimaryParam(pill, parameter, schema[parameter.id], data.params?.[parameter.id]);
    return param ? [{ ...forVariant(param), pill }] : [];
  });
  const primaryKeys = new Set(params.map((param) => param.key));
  const moreParams = parameters
    .filter((parameter) => !primaryKeys.has(parameter.id))
    .flatMap((parameter) => {
      const param = toMoreParam(parameter, schema[parameter.id], data.params?.[parameter.id]);
      return param ? [forVariant(param)] : [];
    });
  return { params, moreParams };
}

/** The run input for the composer's settings: a slider over `enum` options sends its value as the option string. */
export function toRunParams(
  params: readonly GenerateParam[],
  values: Readonly<Record<string, GenerateParamValue>>,
): Record<string, GenerateParamValue> {
  const runParams: Record<string, GenerateParamValue> = { ...values };
  for (const param of params) {
    const value = values[param.key];
    if (param.type === 'integer' && param.options && typeof value === 'number') runParams[param.key] = String(value);
  }
  return runParams;
}

/**
 * What the model's settings accept: each setting's key, type, options and range. Saved settings carry it, and a
 * model whose fingerprint changed since then starts from its defaults, so a value it no longer takes is never
 * sent. Titles and defaults are left out, because a change to them doesn't make a saved value wrong.
 */
export function settingsFingerprint(model: Pick<GenerateModel, 'params' | 'moreParams'>): string {
  return JSON.stringify(
    [...model.params, ...model.moreParams].map((param) => ({
      key: param.key,
      type: param.type,
      options: 'options' in param ? param.options : undefined,
      min: 'min' in param ? param.min : undefined,
      max: 'max' in param ? param.max : undefined,
      auto: 'auto' in param ? param.auto : undefined,
    })),
  );
}

/**
 * The entry's inputs without a frame slot whose key the definition doesn't list as an input. The server sends an
 * unlisted key on to the provider as it is, so the run would fail there. A last frame needs a first frame, so
 * without the first key neither slot shows.
 */
function declaredInputs(inputs: GenerateInputs, handles: ModelBaseNodeData['handles']['input'] | undefined) {
  const { frames, ...rest } = inputs;
  if (!frames) {
    return inputs;
  }
  const declared = new Set(Array.isArray(handles) ? handles : Object.keys(handles ?? {}));
  if (!declared.has(frames.first)) {
    return rest;
  }
  return { ...rest, frames: frames.last && declared.has(frames.last) ? frames : { first: frames.first } };
}

/** Maps a node definition's schema onto the composer's controls. */
function toGenerateModel(slug: string, entry: GenerateModelEntry, definition: NodeDefinition): GenerateModel {
  const data: GenerateNodeDefinitionData = definition.data;
  const baseInput = { ...data.params, ...(entry.variant ? { [VARIANT_KEY]: entry.variant } : {}) };
  const runData: GenerateNodeDefinitionData = { ...data, params: baseInput };
  return {
    id: definition.id,
    slug,
    modelName: entry.modelName,
    variant: entry.variant,
    name: entry.name,
    icon: entry.icon,
    mode: entry.mode,
    ...toGenerateParams(entry, data),
    baseInput,
    freePlanBlocked: isFreePlanBlockedModelNode(runData),
    maxCount: MAX_COUNT,
    inputs: declaredInputs(entry.inputs, data.handles?.input),
  };
}

/** A model with its params left unset, built without its node definition. */
function toSignedOutGenerateModel(slug: string, entry: GenerateModelEntry): GenerateModel {
  return {
    id: '',
    slug,
    modelName: entry.modelName,
    variant: entry.variant,
    name: entry.name,
    icon: entry.icon,
    mode: entry.mode,
    params: [],
    moreParams: [],
    baseInput: {},
    freePlanBlocked: false,
    maxCount: MAX_COUNT,
    inputs: entry.inputs,
  };
}

/** Only built-in rows count: a user can share a saved model, and it keeps the provider name it was saved from. */
function builtInModelName(definition: NodeDefinition): string | undefined {
  const data: GenerateNodeDefinitionData = definition.data;
  return definition.owner === null ? data.model?.name : undefined;
}

/**
 * Whether the definition can run the entry's version. Several built-in rows can share a `model.name`, such as
 * Kling's, each offering other versions in its version setting.
 */
function servesVariant(definition: NodeDefinition, variant: string | undefined): boolean {
  const data: GenerateNodeDefinitionData = definition.data;
  return variant === undefined || (data.schema?.[VARIANT_KEY]?.options?.includes(variant) ?? false);
}

/** The models with only their name and logo, known without loading node definitions. */
export function generateModelLabels(): readonly GenerateModel[] {
  return MODEL_ENTRIES.map(([slug, entry]) => toSignedOutGenerateModel(slug, entry));
}

/**
 * Models the generate page can offer, A to Z by name.
 * Signed-out visitors skip the node definitions, so their models have no params.
 */
export async function listGenerateModels(signedIn: boolean): Promise<readonly GenerateModel[]> {
  if (!signedIn) return generateModelLabels();
  const { data } = await getPublicNodeDefinitions();
  return MODEL_ENTRIES.flatMap(([slug, entry]) => {
    // A `model.name` alone can match several built-in rows, such as Kling's three, so a row that fixes a version
    // takes the one whose version setting offers it. Without a version, the first built-in row wins.
    const definition = data.find(
      (candidate) => builtInModelName(candidate) === entry.modelName && servesVariant(candidate, entry.variant),
    );
    return definition ? [toGenerateModel(slug, entry, definition)] : [];
  });
}

/** What the prompt card sends when Generate is clicked. */
export type GenerateRequest = {
  modelId: string;
  prompt: string;
  mode: GenerateMode;
  /** The model's {@link GenerateModel.baseInput} with the setting values on top. */
  params: Readonly<Record<string, unknown>>;
  /** The attached references and frames, by the key the run sends each one under. */
  inputs: Readonly<Record<string, MediaAsset>>;
  count: number;
};

/** Marks generate page runs so the server can tell them apart from canvas and MCP runs. */
const GENERATOR_HEADERS = { 'x-figma-weave-call-source': 'weave-generator' };

/** A started run with the balance after its charge, or the server's rejection of the inputs. */
type ModelRunResponse = ({ predictionId: string } & ModelRunCredits) | { outcome: 'inputs_required' };

/** No `cost` when the server can't price the run. */
type ModelRunCostResponse = { cost?: number } | { outcome: 'inputs_required' };

export type ModelRunStatus = {
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELED';
  error?: string;
  results?: MediaAsset[];
};

/**
 * Runs the server could not find for this user are left out of `predictions`. The balance is sent only once a
 * requested run has ended, as it may have been refunded.
 */
type ModelRunStatusResponse = ModelRunCredits & {
  predictions: Record<string, ModelRunStatus>;
  resolvedAssets?: ResolvedAssets;
};

/** One past generator run. Cancelled runs are never listed. */
export type GeneratorRunListItem = ModelRunStatus & {
  predictionId: string;
  prompt?: string;
  createdAt: string;
};

/** A page of the user's generator runs in the active workspace, newest first. */
export type GeneratorRunListResponse = {
  runs: GeneratorRunListItem[];
  /** Null on the last page. */
  nextCursor: string | null;
  resolvedAssets?: ResolvedAssets;
};

/**
 * The flat input a run sends. The price quote builds its input here too, so an input added to the run, such as an
 * attached image, is also priced.
 */
export const toModelInput = (params: Readonly<Record<string, unknown>>, prompt: string): Record<string, unknown> => ({
  ...params,
  prompt,
});

/**
 * Starts one run of a model. `id` is its `node_definitions.id`; `input` holds its inputs and params in one flat object.
 * Never retried: a lost response may belong to a run the server already started and billed.
 */
export const runGeneratorModel = (id: string, input: Record<string, unknown>) =>
  safeAxios.post<ModelRunResponse>(
    API_ROUTES.modelRuns.root(),
    { id, input },
    { headers: GENERATOR_HEADERS, 'axios-retry': { retries: 0 } },
  );

/** What one run of a model with `input` would cost, priced the same way as the run itself. */
export const quoteGeneratorModel = (id: string, input: Record<string, unknown>, signal?: AbortSignal) =>
  safeAxios.post<ModelRunCostResponse>(
    API_ROUTES.modelRuns.cost(),
    { id, input },
    { headers: GENERATOR_HEADERS, signal },
  );

/** The canvas's cancel, which also refunds the run. A run that already ended stays as it is. */
export const cancelGeneratorRun = (predictionId: string) =>
  safeAxios.post<void>(API_ROUTES.models.cancelPrediction(), { predictionId });

/** Removes results of finished runs for good, without a refund. Each asset is removed from whichever listed run holds it. */
export const deleteGeneratorResults = (predictionIds: readonly string[], assetIds: readonly string[]) =>
  safeAxios.delete<void>(API_ROUTES.modelRuns.results(), {
    params: { predictionIds: predictionIds.join(','), assetIds: assetIds.join(',') },
  });

export const getModelRunStatuses = (predictionIds: readonly string[], signal?: AbortSignal) =>
  safeAxios.get<ModelRunStatusResponse>(API_ROUTES.modelRuns.status(), {
    params: { predictionIds: predictionIds.join(',') },
    headers: GENERATOR_HEADERS,
    signal,
  });

/** Omit `cursor` for the newest page; pass the previous page's `nextCursor` for the next, older one. */
export const listGeneratorRuns = (cursor?: string, signal?: AbortSignal) =>
  safeAxios.get<GeneratorRunListResponse>(API_ROUTES.modelRuns.root(), {
    params: cursor ? { cursor } : undefined,
    headers: GENERATOR_HEADERS,
    signal,
  });
